import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categoryTabs, categoryGames } from '@/mocks/categories';

export default function Categories() {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredGames = useMemo(() => {
    let games = activeTab === 'all' ? categoryGames : categoryGames.filter((g) => g.category === activeTab);
    if (searchTerm.trim()) {
      games = games.filter((g) => g.name.includes(searchTerm.trim()));
    }
    return games;
  }, [activeTab, searchTerm]);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': '游戏分类 - 银河娱乐官方平台',
    'description': '探索银河娱乐六大游戏品类，电子游艺、真人荷官、体育竞技、棋牌桌游、捕鱼游戏、累积奖池，超过10,000款精品游戏等您来体验。',
    'url': 'https://51xlzxs.cn/categories',
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://51xlzxs.cn/' },
        { '@type': 'ListItem', 'position': 2, 'name': '游戏分类', 'item': 'https://51xlzxs.cn/categories' },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title="游戏分类 - 银河娱乐官方平台"
        description="探索银河娱乐六大游戏品类，电子游艺、真人荷官、体育竞技、棋牌桌游、捕鱼游戏、累积奖池，超过10,000款精品游戏等您来体验。"
        keywords="银河娱乐游戏,电子游艺,真人荷官,体育竞技,棋牌桌游,捕鱼游戏"
        canonicalUrl="https://51xlzxs.cn/categories"
        schemaData={schemaData}
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto pt-8 pb-4">
            <Breadcrumb />
            <div className="px-4 md:px-6 pb-8">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">游戏分类</h1>
              <p className="text-foreground-600 mt-3 text-base">探索超过10,000款精品游戏，找到您的最爱</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-2 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="relative max-w-md">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base w-4 h-4 flex items-center justify-center"></i>
            <input
              type="text"
              placeholder="搜索游戏名称..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>
        </div>

        {/* Games Grid */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {filteredGames.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-search-line text-3xl"></i>
              </div>
              <p className="text-foreground-500 text-base">没有找到相关游戏</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-product-shop>
              {filteredGames.map((game) => (
                <div
                  key={game.id}
                  className="group rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/80 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={game.image}
                      alt={game.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold text-background-50 ${game.tagColor}`}>
                        {game.tag}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{game.name}</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-primary-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                        <span className="text-sm font-medium text-foreground-700">{game.rating}</span>
                      </div>
                      <span className="text-xs text-foreground-500">{game.players}人在玩</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Load More */}
          {filteredGames.length > 0 && (
            <div className="text-center mt-10">
              <button className="px-8 py-3 text-sm font-semibold rounded-full border border-background-300 text-foreground-600 cursor-pointer whitespace-nowrap transition-all duration-300 hover:border-primary-400 hover:text-primary-500">
                加载更多游戏
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}